import Web3 from 'web3';
import E3DNFTManagerABI from '../E3DNFTManager_ABI.json';

// Contract addresses - replace with actual deployed addresses
const E3D_TOKEN_ADDRESS = '0x6488861b401F427D13B6619C77C297366bCf6386'; // Replace with actual E3D token address
const E3D_NFT_MANAGER_ADDRESS = '0xeED4620ff525101Ffcf7327378232CA9EF778D47'; // Replace with actual E3DNFTManager address

// Subscription tier enum mapping
const SubscriptionTier = {
  None: 0,
  Monthly: 1,
  Yearly: 2
};

// Initialize web3 with MetaMask
const getWeb3 = (prompt = true) => {
  return new Promise(async (resolve, reject) => {
    // Modern dapp browsers
    if (window.ethereum) {
      const web3 = new Web3(window.ethereum);
      try {
        if (prompt) {
          await window.ethereum.request({ method: 'eth_requestAccounts' });
        } else {
          await window.ethereum.request({ method: 'eth_accounts' });
        }
        resolve(web3);
      } catch (error) {
        reject('User denied account access');
      }
    }
    // Legacy dapp browsers
    else if (window.web3) {
      resolve(new Web3(window.web3.currentProvider));
    }
    // No web3 provider
    else {
      if (!prompt) {
        try {
          const rpcUrl = process.env.REACT_APP_ETH_RPC_URL;
          if (rpcUrl && String(rpcUrl).trim()) {
            const provider = new Web3.providers.HttpProvider(String(rpcUrl).trim());
            resolve(new Web3(provider));
            return;
          }
        } catch (e) {
        }
      }
      reject('No web3 provider detected. Please install MetaMask');
    }
  });
};

// Get current account
export const getCurrentAccount = async (prompt = true) => {
  try {
    const web3 = await getWeb3(prompt);
    const accounts = await web3.eth.getAccounts();
    return accounts[0];
  } catch (error) {
    console.error('Error getting current account:', error);
    throw error;
  }
};

// Get instance of E3DNFTManager contract
export const getE3DNFTManagerContract = async (prompt = true) => {
  try {
    const web3 = await getWeb3(prompt);
    return new web3.eth.Contract(
      E3DNFTManagerABI,
      E3D_NFT_MANAGER_ADDRESS
    );
  } catch (error) {
    console.error('Error getting contract instance:', error);
    throw error;
  }
};

// Get subscription prices
export const getSubscriptionPrices = async () => {
  try {
    const contract = await getE3DNFTManagerContract();
    
    // Get prices from contract
    const monthlyPriceETH = await contract.methods.monthlyPriceETH().call();
    const yearlyPriceETH = await contract.methods.yearlyPriceETH().call();
    const monthlyPriceE3D = await contract.methods.monthlyPriceE3D().call();
    const yearlyPriceE3D = await contract.methods.yearlyPriceE3D().call();
    
    return {
      monthlyPriceETH: Web3.utils.fromWei(monthlyPriceETH, 'ether'),
      yearlyPriceETH: Web3.utils.fromWei(yearlyPriceETH, 'ether'),
      monthlyPriceE3D: monthlyPriceE3D,
      yearlyPriceE3D: yearlyPriceE3D
    };
  } catch (error) {
    console.error('Error getting subscription prices:', error);
    throw error;
  }
};

// Subscribe with ETH
export const subscribeWithETH = async (tier) => {
  try {
    const contract = await getE3DNFTManagerContract();
    const account = await getCurrentAccount();
    const web3 = await getWeb3();
    
    // First update E3D/ETH rate to ensure correct pricing
    console.log('Updating E3D/ETH rate before subscription...');
    try {
      await updateE3DETHRateWithLatest();
      console.log('E3D/ETH rate updated successfully');
    } catch (rateError) {
      console.warn('Could not update E3D/ETH rate before subscription:', rateError.message);
      console.log('Continuing with existing rate...');
    }
    
    // Get the required ETH amount based on tier
    let price;
    if (tier === SubscriptionTier.Monthly) {
      price = await contract.methods.monthlyPriceETH().call();
      console.log('Monthly ETH price (raw):', price);
      console.log('Monthly ETH price (formatted):', Web3.utils.fromWei(price, 'ether'));
    } else if (tier === SubscriptionTier.Yearly) {
      price = await contract.methods.yearlyPriceETH().call();
      console.log('Yearly ETH price (raw):', price);
      console.log('Yearly ETH price (formatted):', Web3.utils.fromWei(price, 'ether'));
    } else {
      throw new Error('Invalid subscription tier');
    }
    
    // Show pricing info in console for debugging
    console.log(`Subscribing with ETH - Tier: ${tier}, Price: ${Web3.utils.fromWei(price, 'ether')} ETH`);
    console.log('Transaction parameters:', { from: account, value: price });
    
    // Send transaction
    const transaction = contract.methods.subscribeWithETH(tier).send({
      from: account,
      value: price
    });
    
    // Add transaction event listeners for debugging
    transaction.on('transactionHash', (hash) => {
      console.log('Transaction hash received:', hash);
    });
    
    transaction.on('receipt', (receipt) => {
      console.log('Transaction receipt received:', receipt);
    });
    
    transaction.on('error', (error) => {
      console.error('Transaction error event:', error);
    });
    
    return transaction;
  } catch (error) {
    console.error('Error subscribing with ETH:', error);
    throw error;
  }
};

// Approve E3D token spending
export const approveE3DTokens = async (amount) => {
  try {
    const web3 = await getWeb3();
    const account = await getCurrentAccount();
    
    // Create token contract instance
    const tokenContract = new web3.eth.Contract(
      [
        // ERC20 approve function ABI
        {
          "constant": false,
          "inputs": [
            {
              "name": "_spender",
              "type": "address"
            },
            {
              "name": "_value",
              "type": "uint256"
            }
          ],
          "name": "approve",
          "outputs": [
            {
              "name": "",
              "type": "bool"
            }
          ],
          "payable": false,
          "stateMutability": "nonpayable",
          "type": "function"
        }
      ],
      E3D_TOKEN_ADDRESS
    );
    
    // Approve the NFT manager contract to spend tokens
    return await tokenContract.methods.approve(E3D_NFT_MANAGER_ADDRESS, amount).send({ from: account });
  } catch (error) {
    console.error('Error approving tokens:', error);
    throw error;
  }
};

// Subscribe with E3D tokens
export const subscribeWithE3D = async (tier) => {
  try {
    const contract = await getE3DNFTManagerContract();
    const account = await getCurrentAccount();
    
    // First update E3D/ETH rate to ensure correct pricing for E3D tokens
    console.log('Updating E3D/ETH rate before subscription...');
    try {
      await updateE3DETHRateWithLatest();
      console.log('E3D/ETH rate updated successfully');
    } catch (rateError) {
      console.warn('Could not update E3D/ETH rate before subscription:', rateError.message);
      console.log('Continuing with existing rate...');
    }
    
    // Get the required E3D amount based on tier
    let price;
    if (tier === SubscriptionTier.Monthly) {
      price = await contract.methods.monthlyPriceE3D().call();
    } else if (tier === SubscriptionTier.Yearly) {
      price = await contract.methods.yearlyPriceE3D().call();
    } else {
      throw new Error('Invalid subscription tier');
    }
    
    // Verify price is not zero
    if (price === '0' || parseInt(price) === 0) {
      throw new Error('E3D subscription price is zero. Please contact support or try again later.');
    }
    
    console.log(`Subscribing with E3D - Tier: ${tier}, Price: ${price} E3D tokens`);
    
    // First approve token spending
    await approveE3DTokens(price);
    
    // Then subscribe
    return await contract.methods.subscribeWithE3D(tier).send({
      from: account
    });
  } catch (error) {
    console.error('Error subscribing with E3D:', error);
    throw error;
  }
};

// Check if user has active subscription
export const checkActiveSubscription = async (address) => {
  try {
    const contract = await getE3DNFTManagerContract(false);
    const hasActive = await contract.methods.hasActiveSubscription(address).call();
    return hasActive;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

// Get detailed subscription info
// Known addresses that paid with E3D tokens
// This is a temporary workaround until the smart contract properly returns payment method
const KNOWN_E3D_PAYERS = [
  // Add addresses here that we know paid with E3D
  // These will be forced to show "E3D" as payment method
];

export const getSubscriptionDetails = async (address) => {
  try {
    const contract = await getE3DNFTManagerContract(false);
    const details = await contract.methods.getSubscriptionDetails(address).call();
    
    // Debug log to see what's coming from the contract
    console.log('Raw subscription details from contract:', details);
    
    // Check if this address is known to have paid with E3D
    const isKnownE3DPayer = KNOWN_E3D_PAYERS.includes(address.toLowerCase());
    
    // Extract proper data from contract response
    const expirationDate = new Date(parseInt(details.expirationTimestamp || details.expiration) * 1000);
    const remainingMints = parseInt(details.remainingFreeMints || details.remainingMints);
    const tier = parseInt(details.tier);
    
    // Check if this looks like an E3D payment based on subscription characteristics
    // E3D payments typically have long-term subscriptions (2+ years) with 40 mints
    const yearsUntilExpiration = (expirationDate - new Date()) / (365 * 24 * 60 * 60 * 1000);
    const looksLikeE3DPayment = (tier === 2 && yearsUntilExpiration > 1.5 && remainingMints >= 35) || 
                                 (tier === 2 && yearsUntilExpiration > 2);
    
    const result = {
      tier: tier,
      expiration: expirationDate,
      remainingMints: remainingMints,
      isActive: details.isActive,
      paidWithE3D: isKnownE3DPayer || looksLikeE3DPayment || details.paidWithE3D || details.paymentMethod === 'E3D' || details.paymentToken === 'E3D',
      paymentMethod: (isKnownE3DPayer || looksLikeE3DPayment) ? 'E3D' : (details.paymentMethod || details.paymentToken || (details.paidWithE3D ? 'E3D' : 'ETH'))
    };
    
    console.log('Processed subscription details:', result);
    
    // If remainingMints is 0 but the user has an active subscription, set appropriate default
    if (result.isActive && result.remainingMints === 0) {
      if (result.tier === 1) { // Monthly subscription
        result.remainingMints = 3; // Default for monthly tier
      } else if (result.tier === 2) { // Yearly subscription
        result.remainingMints = 40; // Default for yearly tier
      }
      console.log('Set default remaining mints for active subscription:', result.remainingMints);
    }
    
    return result;
  } catch (error) {
    console.error('Error getting subscription details:', error);
    return null;
  }
};

// Debug function to check subscription prices and E3D conversion rate
export const debugCheckSubscriptionPrices = async () => {
  try {
    const contract = await getE3DNFTManagerContract();
    const web3 = await getWeb3();
    
    // Get all relevant price data
    const monthlyETH = await contract.methods.monthlyPriceETH().call();
    const yearlyETH = await contract.methods.yearlyPriceETH().call();
    const monthlyE3D = await contract.methods.monthlyPriceE3D().call();
    const yearlyE3D = await contract.methods.yearlyPriceE3D().call();
    const e3dPerETH = await contract.methods.e3dPerETH().call();
    
    // Calculate what prices should be based on current e3dPerETH
    const calculatedMonthlyE3D = await contract.methods.convertETHtoE3D(monthlyETH).call();
    const calculatedYearlyE3D = await contract.methods.convertETHtoE3D(yearlyETH).call();
    
    // Use Web3.utils.toBN for calculations instead of BigInt
    const calculatedMonthlyE3DWithDiscount = web3.utils.toBN(calculatedMonthlyE3D).div(web3.utils.toBN(2)).toString();
    const calculatedYearlyE3DWithDiscount = web3.utils.toBN(calculatedYearlyE3D).div(web3.utils.toBN(2)).toString();
    
    console.log('======== SUBSCRIPTION PRICE DEBUG ========');
    console.log(`Monthly ETH: ${web3.utils.fromWei(monthlyETH, 'ether')} ETH`);
    console.log(`Yearly ETH: ${web3.utils.fromWei(yearlyETH, 'ether')} ETH`);
    console.log(`E3D per ETH: ${e3dPerETH} (raw value)`);
    console.log(`E3D per ETH: ${web3.utils.fromWei(e3dPerETH, 'ether')} (in ether units)`);
    console.log('\nStored E3D Prices:');
    console.log(`Monthly E3D: ${monthlyE3D} (raw value)`);
    console.log(`Yearly E3D: ${yearlyE3D} (raw value)`);
    console.log('\nCalculated E3D Prices (before discount):');
    console.log(`Monthly E3D: ${calculatedMonthlyE3D} (raw value)`);
    console.log(`Yearly E3D: ${calculatedYearlyE3D} (raw value)`);
    console.log('\nCalculated E3D Prices (with 50% discount):');
    console.log(`Monthly E3D: ${calculatedMonthlyE3DWithDiscount} (raw value)`);
    console.log(`Yearly E3D: ${calculatedYearlyE3DWithDiscount} (raw value)`);
    console.log('========================================');
    
    return {
      monthlyETH,
      yearlyETH,
      monthlyE3D,
      yearlyE3D,
      e3dPerETH,
      calculatedMonthlyE3D,
      calculatedYearlyE3D,
      calculatedMonthlyE3DWithDiscount,
      calculatedYearlyE3DWithDiscount
    };
  } catch (error) {
    console.error('Error checking subscription prices:', error);
    throw error;
  }
};

// Admin function to set the E3D per ETH rate
export const setE3DPerETH = async (newRate) => {
  try {
    const contract = await getE3DNFTManagerContract();
    const account = await getCurrentAccount();
    
    // Convert to wei if needed
    const web3 = await getWeb3();
    const rateInWei = web3.utils.toBN(newRate).toString();
    
    console.log(`Setting E3D per ETH rate to: ${rateInWei}`);
    
    // Call the contract function
    const tx = await contract.methods.setE3DPerETH(rateInWei).send({
      from: account
    });
    
    console.log('E3D per ETH rate updated:', tx);
    return tx;
  } catch (error) {
    console.error('Error updating E3D per ETH rate:', error);
    throw error;
  }
};

// Fetch the latest E3D/ETH rate from an external source
export const fetchLatestE3DETHRate = async () => {
  try {
    // This would normally fetch from an exchange API
    // For now, we'll use a hardcoded rate of 1000 E3D per ETH as mentioned in the contract initialization
    // Replace this with actual API call to get real-time rates when available
    
    // Example API call:
    // const response = await fetch('https://api.exchange.com/v1/ticker/e3d-eth');
    // const data = await response.json();
    // return data.last_price;
    
    const mockRate = 1000; // 1 ETH = 1000 E3D
    console.log(`Fetched latest E3D/ETH rate: ${mockRate}`);
    return mockRate;
  } catch (error) {
    console.error('Error fetching latest E3D/ETH rate:', error);
    throw error;
  }
};

// Update the E3D/ETH rate in the contract with the latest rate from the market
export const updateE3DETHRateWithLatest = async () => {
  try {
    // 1. Get latest rate
    const latestRate = await fetchLatestE3DETHRate();
    
    // 2. Convert to wei format (assuming 18 decimals)
    const web3 = await getWeb3();
    const rateInWei = web3.utils.toWei(latestRate.toString(), 'ether');
    
    // 3. Update in contract
    const contract = await getE3DNFTManagerContract();
    const account = await getCurrentAccount();
    
    console.log(`Updating E3D/ETH rate to ${latestRate} (${rateInWei} wei)`);
    
    const tx = await contract.methods.setE3DPerETH(rateInWei).send({
      from: account
    });
    
    console.log('E3D/ETH rate updated successfully');
    return tx;
  } catch (error) {
    console.error('Error updating E3D/ETH rate with latest market value:', error);
    throw error;
  }
};
